import React, { useEffect, useState } from 'react';
import {type TooltipRenderProps} from 'react-joyride';
import { nodeToString } from '../../utils/helpers';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';

interface CustomTooltipProps extends TooltipRenderProps {
  handleTourSkip: () => void;
}

const CustomTooltip = ({
    step,
    closeProps,
    primaryProps,
    tooltipProps,
    handleTourSkip,
  }: CustomTooltipProps) => {

    const [visible, setVisible] = useState(false);

    useEffect(() => {
    // Trigger the animation after mount
    setTimeout(() => setVisible(true), 10);
  }, []);
  
    let title = "";
    let description: string[] = [];
    const contentElement = step.content as React.ReactElement;
    if (contentElement.props.children){
      if (Array.isArray(contentElement.props.children)){
        title = nodeToString(contentElement.props.children[0]?.props?.children);
        description = contentElement.props.children.slice(1).map(nodeToString);
      } else {
        description = [nodeToString(contentElement.props.children)];
      }
    }
    const isBlueLeftTooltip = step.placement === 'left';
    const isBlueBottomTooltip = step.placement === 'bottom';
    const isBlueTopTooltip = step.placement === 'top';
    const isLeftStart = step.placement && step.placement.includes('left-start');
    const isRightStart = step.placement && step.placement.includes('right-start');  
   
    if (isBlueLeftTooltip) {
      return (
        <Box
          {...tooltipProps}
          sx={{
            background: '#3366FF',
            color: '#fff',
            height: { xs: 40, md: 55},
            minWidth: 200,
            maxWidth: 270,
            boxSizing: 'border-box',
            textAlign: 'center',
            p: '16px 16px 16px 16px',
            position: 'relative',
            zIndex: 2500,
            boxShadow: 'none',
            transition: 'all 1s cubic-bezier(.4,0,.2,1)',
            opacity: visible ? 1 : 0.2,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >        
           <Box
            sx={{
            position: 'absolute',
            top: '50%',
            right: '-28px',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '28.5px solid transparent',
            borderBottom: '28.5px solid transparent',
            borderLeft: '28.5px solid #3366FF',
            }}
        />      
          {description.map((para: string, idx: number) => (
            <Box key={idx} sx={{ 
              fontSize: { xs: 12, sm: 13, md: 14}, 
              lineHeight: 1.3, 
              mt: 0.5 
            }}>
              {para}
            </Box>
          ))}
        </Box>
      );
    }

    if (isBlueBottomTooltip || isBlueTopTooltip) {

      return (
        <Box
          {...tooltipProps}
          sx={{
            background: '#3366FF',
            color: '#fff',
            maxWidth: { xs: 180, md: 270},
            minWidth: { xs: 180, md: 220},
            boxSizing: 'border-box',
            textAlign: 'center',
            p: '16px 8px 12px 8px',
            position: 'relative',
            zIndex: 2500,
            boxShadow: 'none',
            transition: 'all 1s cubic-bezier(.4,0,.2,1)',
            opacity: visible ? 1 : 0.2,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
        {isBlueBottomTooltip ? (
              <Box sx={{ 
                position: 'absolute',
                top: -20,
                left: '50%',
                transform: 'translateX(-50%)',
                borderLeft: '20px solid transparent',
                borderRight: '20px solid transparent',
                borderBottom: '20px solid #3366FF'
              }} />
            ) : (  
          <Box sx={{ 
            position: 'absolute',
            bottom: -20,
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: '20px solid transparent',
            borderRight: '20px solid transparent',
            borderTop: '20px solid #3366FF'
          }} />
        )}

          {title && (
          <Box 
          sx={{ 
            fontWeight: 500, 
            fontSize: { xs: 12, sm: 13}, 
            lineHeight: 1.3 
          }}>
            {title}
          </Box>
          )}
          {description.map((para: string, idx: number) => (
            <Box key={idx} sx={{ 
              fontSize: { xs: 12, sm: 13, md: 14}, 
              lineHeight: 1.3, 
              mt: 0.5 
            }}>
              {para}
            </Box>
          ))}
        </Box>
      );
    }
  
    return (
      <Box
        {...tooltipProps}
        sx={{
          background: '#0E101A',
          p: '24px 24px 20px 24px',
          color: '#fff',
          borderRadius: { xs: 1, sm: 2, md: 3 },
          boxShadow: '0 4px 32px rgba(0,0,0,0.25)',
          position: 'relative',
          width: { xs: '200px', sm: '350px', md: '390px' },
          boxSizing: 'border-box',
          zIndex: 2500,
          transition: 'all 1s cubic-bezier(.4,0,.2,1)',
          opacity: visible ? 1 : 0.2,
          transform: visible ? 'translateY(0)' : 'translateY(40px)',
        }}
      >
        {/* Arrow */}
        {isLeftStart && (
        <Box sx={{ 
          position: 'absolute',
          right: -20,
          width: 0,
          height: 0,
          borderTop: '20px solid transparent',
          borderBottom: '20px solid transparent',
          borderLeft: '20px solid #0E101A'
        }} />
        )}
        {isRightStart && (
        <Box sx={{ 
          position: 'absolute',
          left: -20,
          width: 0,
          height: 0,
          borderTop: '20px solid transparent',
          borderBottom: '20px solid transparent',
          borderRight: '20px solid #0E101A'
        }} />
        )}

      {/* Close (X) button */}
      <IconButton
        {...closeProps}
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          color: '#fff',
          fontSize: 24,
          fontWeight: 600,
        }}
        aria-label="Close"
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
        {/* Title */}
        <Box 
        sx={{ 
          fontWeight: 500,
          width: isLeftStart ? '78%' : '100%',
          fontFamily: 'InterMedium',
          fontSize: { xs: 16, sm: 20, md: 22}, 
          p: { xs: 0, sm: 0.4},
          lineHeight: 1.2, 
          mb: 2.5,
        }}
        >
          {title}
        </Box>
  
        {/* Description (split into paragraphs if possible) */}
        {description.map((para: string, idx: number) => (
          <Box 
          key={idx} 
          sx={{ 
            fontSize: { xs: 12, sm: 13, md: 15}, 
            lineHeight: 1.4, 
            mb: 2,
            fontFamily: 'InterRegular'
          }}
          >
            {para}
          </Box>
        ))}

    <Box sx={{display: 'flex', gap: 2}}>

    {(isLeftStart) && (
    /* Skip Button */
    <Button
        variant="contained"
        sx={{
        width: '100%',
        height: 44,
        mt: 3,
        background: '#000',
        color: '#fff',
        borderRadius: 1.5,
        border: '1px solid #fff',
        fontWeight: 600,
        fontSize: { xs: 12, sm: 16},
        '&:hover': {
            background: 'none',
            border: '1px solid #7B2FF2',
        },
        }}
        onClick={handleTourSkip}
        >
        Skip
    </Button>
    )
    }

    {/* Next Button */}
    <Button
        {...primaryProps}
        variant="contained"
        sx={{
            width: '100%',
            height: 44,
            mt: 3,
            background: '#A259FF',
            color: '#fff',
            borderRadius: 1.5,
            fontWeight: 600,
            fontSize: { xs: 12, sm: 16},
            '&:hover': {
            background: '#7B2FF2',
            },
        }}
        >
        Next
    </Button>
    </Box>
      </Box>
    );
  };
  
export const createSubscribedCustomTooltipWrapper = (handleTourSkip: () => void) => {
    return function CustomTooltipWrapperWithProps(props: TooltipRenderProps) {
      return <CustomTooltip {...props} handleTourSkip={handleTourSkip} />;
    };
  };
  